import {
  Suspense,
  lazy,
  useEffect
} from 'react'
import {
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom'
import {
 Layout
} from './components/Layout'
import {
 LoginPage
} from './components/LoginPage'
import {
 CallbackPage
} from './components/CallbackPage'
import {
 PendingPage
} from './components/PendingPage'
import {
 SettingsPage
} from './components/SettingsPage'
import {
 SlackCallbackPage
} from './components/SlackCallbackPage'
import {
 SlackPersonalCallbackPage
} from './components/SlackPersonalCallbackPage'
import {
 ZoomCallbackPage
} from './components/ZoomCallbackPage'
import {
 PrivacyPolicyPage
} from './components/PrivacyPolicyPage'
import {
 SupportPage
} from './components/SupportPage'
import {
 SlackLandingPage
} from './components/SlackLandingPage'
import {
 OnboardingPage
} from './components/onboarding/OnboardingPage'
import {
  SettingsAlias
} from './components/SettingsAlias'
import {
  CALLBACK_ROUTE,
  LOGIN_ROUTE,
  PENDING_ROUTE,
  VIEWS_ROUTE,
  VIEW_RENDERER_ROUTE,
  VISUALIZATION_RENDERER_ROUTE,
  ONBOARDING_DEMO_ROUTE,
  isOnboardingDemoPath
} from './utils/routes'
import {
 SchedulingBookingPage
} from './components/SchedulingBookingPage'
import {
 ConciergeAvailabilityPage
} from './components/ConciergeAvailabilityPage'
import {
 FlightOptionsPage
} from './components/FlightOptionsPage'
import {
 StayOptionsPage
} from './components/StayOptionsPage'
import {
 TripsMockPage,
 TripsPage
} from './components/TripsMockPage'
import {
  ConnectEmailPage,
} from './components/ConnectEmailPage'
import {
 ZoomLandingPage
} from './components/ZoomLandingPage'
import {
 PricingPage
} from './components/PricingPage'
import {
 BillingSuccessPage
} from './components/BillingSuccessPage'
import {
 BillingPortalRedirectPage
} from './components/BillingPortalRedirectPage'
import {
  TravelCardSetupResultPage
} from './components/TravelCardSetupResultPage'
import {
 EnterSecretPage
} from './components/EnterSecretPage'
import {
 VaultEnterSecretPage
} from './components/VaultEnterSecretPage'
import {
 BookHotelPage
} from './components/BookHotelPage'
import {
 ReferralPage
} from './components/ReferralPage'
import {
 AmbassadorsPage
} from './components/AmbassadorsPage'
import {
 StatsPage
} from './components/StatsPage'
import {
 TeamPage
} from './components/TeamPage'
import {
  AuthProvider
} from './hooks/useAuth'
import {
  trackPageView
} from './analytics/posthog'
import {
  captureAmbassadorEntry,
  captureReferralFromUrl
} from './utils/referral'
import {
  capturePortalIntentFromUrl
} from './utils/billing'
import {
  ExecutiveVisualizationPage
} from './components/ExecutiveVisualizationPage'
import {
  ProtectedRoute, SetupGuard
} from './components/routeGuards'
import {
  ViewsIndexPage,
} from './components/views/ViewsIndexPage'

// Loaded on open, so json-render stays out of the bundle every other page pays for.
const ViewPage = lazy(async () => ({
  default: (await import('./components/views/ViewPage')).ViewPage,
}))
const ViewRendererPage = lazy(async () => ({
  default: (await import('./components/views/ViewRendererPage')).ViewRendererPage,
}))
// Loaded on open so the demo's fixtures stay out of the bundle real users download.
const OnboardingDemoPage = lazy(async () => ({
  default: (await import('./components/onboarding/demo/OnboardingDemoPage')).OnboardingDemoPage,
}))
// Loaded on open so the Terms' ~20KB of legal text stays out of the bundle every
// user downloads.
const TermsOfServicePage = lazy(async () => ({
  default: (await import('./components/TermsOfServicePage')).TermsOfServicePage,
}))
// Both URLs serve the same Terms: /terms-new is the one Google approved and every
// link uses, and /terms keeps working for anyone holding the old link.
const termsOfServicePage = (
  <Suspense fallback={<div className="min-h-screen" />}>
    <TermsOfServicePage />
  </Suspense>
)

// Capture a referral link's ?ref= / ?checkout=true as early as possible — before
// auth redirects can strip the query string. Persists to web storage that
// survives the Google OAuth round-trip; the discount is enforced server-side.
captureReferralFromUrl()
// Same for a dunning email's ?portal=true card-update deep link.
capturePortalIntentFromUrl()

function PageTracker(): null {
  const location = useLocation()

  useEffect(() => {
    // Reached only on the way to reloading into the demo, which records nothing.
    if (isOnboardingDemoPath(location.pathname)) return
    trackPageView()
  }, [location.pathname])

  return null
}

// Sign-in resumes a remembered demo link in-app. The demo runs outside this
// tree and needs a page on which analytics never started, so load it fresh
// (PageTracker skips this path, so nothing is recorded on the way).
function OnboardingDemoReload(): null {
  useEffect(() => {
    window.location.reload()
  }, [])
  return null
}

function ReferralRoute(): JSX.Element {
  // The referral page: OAuth-gated, but intentionally NOT behind SetupGuard —
  // someone arriving to refer should land here straight after login without
  // being bounced through onboarding.
  //
  const { search } = useLocation()
  // Deliberately not in an effect: a logged-out visit renders <Navigate> below
  // instead of ReferralPage, so the marker has to be stashed before the auth
  // redirect strips the query string. Writing the same value is idempotent.
  captureAmbassadorEntry(search)
  return (
    <ProtectedRoute>
      <ReferralPage />
    </ProtectedRoute>
  )
}

function App() {
  if (window.location.pathname === VISUALIZATION_RENDERER_ROUTE) {
    return <ExecutiveVisualizationPage />
  }
  if (window.location.pathname === VIEW_RENDERER_ROUTE) {
    return (
      <Suspense fallback={null}>
        <ViewRendererPage />
      </Suspense>
    )
  }
  // Outside AuthProvider on purpose: the demo's stand-in user must never be
  // checked against, or written over, the browser's real session. The page
  // checks that session itself before letting anyone in.
  if (isOnboardingDemoPath(window.location.pathname)) {
    return (
      <Suspense fallback={null}>
        <OnboardingDemoPage />
      </Suspense>
    )
  }

  return (
    <AuthProvider>
      <PageTracker />
      <Routes>
        {/* Login page renders without the standard layout */}
        <Route path={LOGIN_ROUTE} element={<LoginPage />} />
        {/* Onboarding wizard renders full-page without the standard layout */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route path={ONBOARDING_DEMO_ROUTE} element={<OnboardingDemoReload />} />

        {/* Public accommodation options map - no auth required (token in path) */}
        <Route path="/stays/:token" element={<StayOptionsPage />} />

        {/* Public flight options - no auth required (token in path) */}
        <Route path="/flights/:token" element={<FlightOptionsPage />} />

        {/* Public live trips page - no auth required (signed token in path) */}
        <Route path="/trips/:token" element={<TripsPage />} />

        {/* Deterministic concept fixture for visual review */}
        <Route path="/trips/mock" element={<TripsMockPage />} />

        {/* Public "guide your IT" email-DNS setup page - token in query string */}
        <Route path="/connect-email" element={<ConnectEmailPage />} />

        {/* Public scheduling link - no auth required */}
        <Route path="/s/:linkId" element={<SchedulingBookingPage />} />
        {/* Per-invitee URL for invitee-bound links — token in path */}
        <Route path="/s/:linkId/i/:token" element={<SchedulingBookingPage />} />

        {/* Public concierge availability entry - no auth required (token in path) */}
        <Route path="/cc/:token" element={<ConciergeAvailabilityPage />} />

        {/* Magic-link secret entry for user-authored integrations. Auth is
            carried by the signed token in the query string. */}
        <Route path="/integrations/enter-secret" element={<EnterSecretPage />} />

        {/* Magic-link entry for the secure personal-details vault. Auth is the
            signed token in the query string. */}
        <Route path="/vault/enter-secret" element={<VaultEnterSecretPage />} />

        {/* Public signed handoff that mints a fresh Stripe portal session. */}
        <Route path="/billing/portal" element={<BillingPortalRedirectPage />} />

        {/* Stripe-hosted travel-card setup returns here; no app login required. */}
        <Route path="/travel-card/saved" element={<TravelCardSetupResultPage />} />

        {/* All other routes use the standard layout */}
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path={CALLBACK_ROUTE} element={<CallbackPage />} />
          <Route path={PENDING_ROUTE} element={<PendingPage />} />
          <Route path="/terms" element={termsOfServicePage} />
          <Route path="/terms-new" element={termsOfServicePage} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/slack" element={<SlackLandingPage />} />
          <Route path="/zoom" element={<ZoomLandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/ambassadors" element={<AmbassadorsPage />} />
          <Route
            path="/book-hotel"
            element={
              <ProtectedRoute>
                <BookHotelPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute>
                <BillingSuccessPage />
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<SettingsAlias />} />
          <Route
            path="/integrations"
            element={
              // The logged-out bounce carries its connect-inbox intent via
              // LOGIN_SEARCH_BY_ROUTE in utils/routes.ts (GET-9017).
              <ProtectedRoute>
                <SetupGuard>
                  <SettingsPage />
                </SetupGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stats"
            element={
              <ProtectedRoute>
                <SetupGuard>
                  <StatsPage />
                </SetupGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <SetupGuard>
                  <TeamPage />
                </SetupGuard>
              </ProtectedRoute>
            }
          />
          <Route path="/refer" element={<ReferralRoute />} />
          {/* Pages the assistant built (GET-15593). Signed in, but outside
              SetupGuard: the link must open before onboarding is finished. */}
          <Route
            path={VIEWS_ROUTE}
            element={(
              <ProtectedRoute>
                <ViewsIndexPage />
              </ProtectedRoute>
            )}
          />
          <Route
            path={`${VIEWS_ROUTE}/:viewId`}
            element={(
              <ProtectedRoute>
                <Suspense fallback={<div className="min-h-screen" />}>
                  <ViewPage />
                </Suspense>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/integrations/slack/callback"
            element={<SlackCallbackPage />}
          />
          <Route
            path="/integrations/slack/personal/callback"
            element={<SlackPersonalCallbackPage />}
          />
          <Route
            path="/integrations/zoom/callback"
            element={<ZoomCallbackPage />}
          />
        </Route>
      </Routes>
    </AuthProvider>
  )
}

export default App
